<template>
    <div class="aue animated fadeIn" v-if="ready">
        <!-- Page Header -->
        <div class="aue-page-header">
            <button class="aue-back-btn" @click="$router.push({ path: '/admin/iam' })">
                <b-icon icon="arrow-left" class="mr-1"></b-icon>
                {{ $t('back') }}
            </button>
        </div>

        <div class="aue-grid">
            <!-- Left Card: User Details -->
            <div class="aue-card">
                <!-- Card Header -->
                <div class="aue-card-header">
                    <div class="aue-card-icon">
                        <b-icon icon="key-fill" font-scale="1.3" />
                    </div>
                    <div class="aue-header-info">
                        <h2 class="aue-card-title">{{ $t('editApiUser') || 'Edit API User' }}</h2>
                        <p class="aue-card-subtitle">{{ model.username }}</p>
                    </div>
                </div>

                <!-- Form Body -->
                <div class="aue-card-body">
                    <InputField
                        name="name"
                        :label="$t('name')"
                        v-model="model.username"
                        @error="fieldError"
                        :disabled="true"
                        :rules="{ required: true, length: { min: 2 } }"
                    />

                    <b-form-group :label="$t('role')">
                        <v-select
                            :options="roles"
                            v-model="model.roles"
                            multiple
                            label="name"
                            :disabled="!canManipulate"
                        />
                    </b-form-group>

                    <div class="aue-form-actions" v-if="canManipulate">
                        <button type="button" class="aue-save-btn" @click="save">
                            <b-icon icon="check2" class="mr-2"></b-icon>
                            {{ $t('save') }}
                        </button>
                    </div>
                </div>

                <!-- Danger Zone -->
                <div class="aue-danger-zone" v-if="canManipulate">
                    <div class="aue-danger-header">
                        <b-icon icon="exclamation-triangle" class="mr-2"></b-icon>
                        {{ $t('dangerZone') || 'Danger Zone' }}
                    </div>
                    <div class="aue-danger-body">
                        <p class="aue-danger-warning">{{ $t('deleteApiUserWarning') || 'Permanently delete this API user and all associated secrets.' }}</p>
                        <button type="button" class="aue-delete-btn" @click="remove">
                            <b-icon icon="trash3" class="mr-2"></b-icon>
                            {{ $t('delete') }}
                        </button>
                    </div>
                </div>
            </div>

            <!-- Right Card: API Secrets -->
            <div class="aue-card" v-if="canManipulate">
                <!-- Secrets Header -->
                <div class="aue-card-header aue-card-header--secrets">
                    <div class="aue-card-icon aue-card-icon--secrets">
                        <b-icon icon="shield-lock" font-scale="1.3" />
                    </div>
                    <div class="aue-header-info">
                        <h2 class="aue-card-title">{{ $t('apiSecrets') || 'API Secrets' }}</h2>
                        <p class="aue-card-subtitle">{{ model.secrets ? model.secrets.length : 0 }} / 5</p>
                    </div>
                </div>

                <div class="aue-card-body">
                    <!-- Toolbar -->
                    <div class="aue-secrets-toolbar">
                        <button
                            class="aue-generate-btn"
                            @click="generateNewSecret"
                            :disabled="model.secrets && model.secrets.length >= 5"
                        >
                            <b-icon icon="plus-circle" class="mr-2"></b-icon>
                            {{ $t('generateNewSecret') }}
                        </button>

                        <label class="aue-forever-toggle">
                            <b-form-checkbox v-model="forever" switch />
                            <span class="aue-forever-label">
                                Forever
                                <small v-if="forever" class="aue-forever-desc">{{ $t('authTokenForeverDesc') }}</small>
                            </span>
                        </label>
                    </div>

                    <!-- Empty State -->
                    <div v-if="!model.secrets || model.secrets.length === 0" class="aue-empty-secrets">
                        <b-icon icon="shield-lock" font-scale="2" class="mb-2" />
                        <p>{{ $t('noSecrets') || 'No API secrets yet' }}</p>
                    </div>

                    <!-- Secrets List -->
                    <div v-else class="aue-secrets-list">
                        <div class="aue-secret-card" v-for="secret in model.secrets" :key="secret.uuid">
                            <div class="aue-secret-top">
                                <code class="aue-secret-uuid">{{ secret.uuid }}</code>
                                <div class="aue-secret-actions">
                                    <button class="aue-token-btn" @click="generateToken(secret)">
                                        <b-icon icon="clipboard-plus" class="mr-1"></b-icon>
                                        {{ $t("apiUserGenerateToken") }}
                                    </button>
                                    <button class="aue-revoke-btn" @click="revoke(secret)">
                                        <b-icon icon="x-circle" class="mr-1"></b-icon>
                                        {{ $t('revoke') }}
                                    </button>
                                </div>
                            </div>

                            <div v-if="secret.secret" class="aue-secret-value">
                                <label>{{ $t('secret') }}</label>
                                <b-input :value="secret.secret" disabled class="aue-secret-input" />
                                <small class="aue-secret-hint">{{ $t('apiUserSecretDescription') }}</small>
                            </div>

                            <div class="aue-secret-meta">
                                <span v-if="secret.auth_at" class="aue-meta-item">
                                    <b-icon icon="clock" class="mr-1"></b-icon>
                                    {{ $t('lastTimeUsed') }}: {{ $toUserTimeZone(secret.auth_at) }}
                                </span>
                                <span v-if="secret.forever" class="aue-meta-badge">
                                    <b-icon icon="infinity" class="mr-1"></b-icon>
                                    {{ $t('authTokenForeverDesc') }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Confirm Dialog -->
        <main-dialog
            :show="confirmDialog.show"
            :title="confirmDialog.title"
            @close="confirmDialog.show = false"
        >
            <p>{{ confirmDialog.message }}</p>
            <template #footer>
                <b-button variant="outline-secondary" @click="confirmDialog.show = false">{{ $t('cancel') }}</b-button>
                <b-button :variant="confirmDialog.variant || 'danger'" @click="confirmDialog.action">
                    {{ confirmDialog.actionLabel }}
                </b-button>
            </template>
        </main-dialog>
    </div>
</template>

<style scoped lang="scss">
.aue {
    padding: 0;
}

/* ── Page Header ───────────────────────────────────────────────────── */
.aue-page-header {
    margin-bottom: 1.75rem;
}

.aue-back-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.45rem 0.9rem;
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--text-secondary);
    background: var(--bg-card);
    border: 1px solid var(--border-default);
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        color: var(--text-primary);
        border-color: var(--border-hover);
        background: var(--bg-card-secondary);
    }
}

/* ── Grid ─────────────────────────────────────────────────────────── */
.aue-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.5rem;
    align-items: start;

    @media (max-width: 992px) {
        grid-template-columns: 1fr;
    }
}

/* ── Card ──────────────────────────────────────────────────────────── */
.aue-card {
    background: var(--bg-card);
    border: 1px solid var(--border-default);
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.12);
}

/* ── Card Header ───────────────────────────────────────────────────── */
.aue-card-header {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1.5rem 1.75rem;
    background: linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(118, 75, 162, 0.08) 100%);
    border-bottom: 1px solid var(--border-default);
}

.aue-card-header--secrets {
    background: linear-gradient(135deg, rgba(72, 187, 120, 0.08) 0%, rgba(56, 239, 125, 0.06) 100%);
}

.aue-card-icon {
    width: 52px;
    height: 52px;
    border-radius: 14px;
    background: linear-gradient(135deg, var(--primary-start), var(--primary-end));
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: #fff;
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.aue-card-icon--secrets {
    background: linear-gradient(135deg, #48bb78, #38ef7d);
    box-shadow: 0 4px 12px rgba(72, 187, 120, 0.3);
}

.aue-header-info {
    flex: 1;
    min-width: 0;
}

.aue-card-title {
    font-size: 1rem;
    font-weight: 700;
    color: var(--text-primary);
    margin: 0 0 0.2rem;
    line-height: 1.2;
}

.aue-card-subtitle {
    font-size: 0.85rem;
    color: var(--text-secondary);
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

/* ── Card Body ─────────────────────────────────────────────────────── */
.aue-card-body {
    padding: 1.75rem;
}

/* ── Form Actions ──────────────────────────────────────────────────── */
.aue-form-actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 1.5rem;
    padding-top: 1.25rem;
    border-top: 1px solid var(--border-default);
}

.aue-save-btn {
    display: inline-flex;
    align-items: center;
    padding: 0.65rem 1.5rem;
    font-size: 0.9rem;
    font-weight: 600;
    border: none;
    border-radius: 10px;
    background: linear-gradient(135deg, var(--primary-start), var(--primary-end));
    color: #fff;
    cursor: pointer;
    box-shadow: 0 4px 14px rgba(102, 126, 234, 0.35);
    transition: all 0.25s ease;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 20px rgba(102, 126, 234, 0.5);
    }

    &:active {
        transform: translateY(0);
    }
}

/* ── Secrets Toolbar ──────────────────────────────────────────────── */
.aue-secrets-toolbar {
    display: flex;
    align-items: center;
    gap: 1.25rem;
    margin-bottom: 1.25rem;
    flex-wrap: wrap;
}

.aue-generate-btn {
    display: inline-flex;
    align-items: center;
    padding: 0.55rem 1.1rem;
    font-size: 0.85rem;
    font-weight: 600;
    border: 1px solid rgba(72, 187, 120, 0.5);
    border-radius: 10px;
    background: rgba(72, 187, 120, 0.08);
    color: rgb(72, 187, 120);
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover:not(:disabled) {
        background: rgba(72, 187, 120, 0.15);
        border-color: rgb(72, 187, 120);
        transform: translateY(-1px);
    }

    &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
    }
}

.aue-forever-toggle {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;
    margin: 0;
}

.aue-forever-label {
    font-size: 0.85rem;
    font-weight: 500;
    color: var(--text-secondary);
}

.aue-forever-desc {
    display: block;
    font-size: 0.75rem;
    color: var(--text-secondary);
    opacity: 0.7;
    margin-top: 0.1rem;
}

/* ── Empty State ──────────────────────────────────────────────────── */
.aue-empty-secrets {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 2rem 1rem;
    color: var(--text-secondary);
    opacity: 0.6;

    p {
        margin: 0;
        font-size: 0.9rem;
    }
}

/* ── Secrets List ─────────────────────────────────────────────────── */
.aue-secrets-list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}

.aue-secret-card {
    background: var(--bg-card-secondary);
    border: 1px solid var(--border-default);
    border-radius: 12px;
    padding: 1rem 1.25rem;
    transition: all 0.2s ease;

    &:hover {
        border-color: var(--border-hover);
    }
}

.aue-secret-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    flex-wrap: wrap;
}

.aue-secret-uuid {
    font-size: 0.8rem;
    color: var(--text-secondary);
    background: rgba(102, 126, 234, 0.08);
    padding: 0.25rem 0.6rem;
    border-radius: 6px;
    word-break: break-all;
}

.aue-secret-actions {
    display: flex;
    gap: 0.5rem;
    flex-shrink: 0;
}

.aue-token-btn {
    display: inline-flex;
    align-items: center;
    padding: 0.35rem 0.75rem;
    font-size: 0.8rem;
    font-weight: 500;
    border: 1px solid rgba(72, 187, 120, 0.4);
    border-radius: 8px;
    background: rgba(72, 187, 120, 0.06);
    color: rgb(72, 187, 120);
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background: rgba(72, 187, 120, 0.12);
        border-color: rgb(72, 187, 120);
    }
}

.aue-revoke-btn {
    display: inline-flex;
    align-items: center;
    padding: 0.35rem 0.75rem;
    font-size: 0.8rem;
    font-weight: 500;
    border: 1px solid rgba(220, 53, 69, 0.4);
    border-radius: 8px;
    background: rgba(220, 53, 69, 0.06);
    color: rgb(220, 53, 69);
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background: rgba(220, 53, 69, 0.12);
        border-color: rgb(220, 53, 69);
    }
}

/* ── Secret Value ─────────────────────────────────────────────────── */
.aue-secret-value {
    margin-top: 0.85rem;
    padding-top: 0.85rem;
    border-top: 1px solid var(--border-default);

    label {
        font-size: 0.75rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: var(--text-secondary);
        margin-bottom: 0.35rem;
    }
}

.aue-secret-input {
    font-family: monospace;
    font-size: 0.8rem;
}

.aue-secret-hint {
    display: block;
    font-size: 0.75rem;
    color: var(--text-secondary);
    opacity: 0.7;
    margin-top: 0.35rem;
}

/* ── Secret Meta ──────────────────────────────────────────────────── */
.aue-secret-meta {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-top: 0.75rem;
    flex-wrap: wrap;
}

.aue-meta-item {
    display: inline-flex;
    align-items: center;
    font-size: 0.75rem;
    color: var(--text-secondary);
}

.aue-meta-badge {
    display: inline-flex;
    align-items: center;
    font-size: 0.7rem;
    font-weight: 600;
    color: rgb(102, 126, 234);
    background: rgba(102, 126, 234, 0.1);
    padding: 0.15rem 0.5rem;
    border-radius: 6px;
}

/* ── Danger Zone ──────────────────────────────────────────────────── */
.aue-danger-zone {
    border-top: 1px solid rgba(220, 53, 69, 0.25);
    background: rgba(220, 53, 69, 0.04);
}

.aue-danger-header {
    display: flex;
    align-items: center;
    padding: 0.9rem 1.75rem;
    font-size: 0.8rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: rgba(220, 53, 69, 0.8);
    border-bottom: 1px solid rgba(220, 53, 69, 0.15);
}

.aue-danger-body {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1.5rem;
    padding: 1.25rem 1.75rem;
}

.aue-danger-warning {
    font-size: 0.85rem;
    color: var(--text-secondary);
    margin: 0;
    line-height: 1.5;
}

.aue-delete-btn {
    display: inline-flex;
    align-items: center;
    white-space: nowrap;
    padding: 0.55rem 1.25rem;
    font-size: 0.875rem;
    font-weight: 600;
    border: 1px solid rgba(220, 53, 69, 0.5);
    border-radius: 10px;
    background: rgba(220, 53, 69, 0.08);
    color: rgb(220, 53, 69);
    cursor: pointer;
    transition: all 0.2s ease;
    flex-shrink: 0;

    &:hover {
        background: rgba(220, 53, 69, 0.15);
        border-color: rgb(220, 53, 69);
        transform: translateY(-1px);
    }

    &:active {
        transform: translateY(0);
    }
}
</style>

<script>
import Input from "@/components/form/Input"
import VSelect from "vue-select"

export default {
    name: "EditApiUser",
    components: {
        InputField: Input,
        VSelect
    },
    data: function () {
        return {
            ready: false,
            errors: [],
            model: {
                roles: [],
                secrets: []
            },
            forever: false,
            confirmDialog: {
                show: false,
                title: '',
                message: '',
                variant: 'danger',
                actionLabel: '',
                action: () => {}
            }
        }
    },

    computed: {
        roles() {
            return this.$store.getters.getRoles
        },

        modelFormated() {
            return {
                roles: this.model.roles.map(r => r.uuid)
            }
        },

        canManipulate() {
            return !this.model.dependency
        }
    },

    methods: {
        save(e) {
            e.preventDefault()

            if (this.checkForErrors()) {
                this.$mainDialog.msgBoxOk(this.$t('fieldsNotValid'))
                return
            }

            this.$http.patch("/v1/user/api-user/" + this.$route.params.id, this.modelFormated).then(() => {
                this.$bvToast.toast(this.$t('success'), {
                    variant: 'success',
                    title: "Success"
                })
            }).catch(error => {
                if (error.response) {
                    if (error.response.status == 500) {
                        this.$serverErrorModal(error)
                        return
                    }
                    this.$mainDialog.msgBoxOk(error.response.data.message)
                }
            })
        },

        generateNewSecret() {
            this.confirmDialog = {
                show: true,
                title: this.$t('generateNewSecret'),
                message: `${this.$t('areYouSure')}?`,
                variant: 'success',
                actionLabel: this.$t('generateNewSecret'),
                action: () => this.doGenerateSecret()
            }
        },

        doGenerateSecret() {
            this.confirmDialog.show = false
            const popup = this.$loadingPopup()
            this.$http.post("/v1/user/api-user/secret/" + this.$route.params.id, {
                forever: this.forever
            }).then((resp) => {
                this.model.secrets.push(resp.data)
            }).catch(error => {
                if (error.response) {
                    if (error.response.status == 500) {
                        this.$serverErrorModal(error)
                        return
                    }
                    this.$mainDialog.msgBoxOk(error.response.data.message)
                }
            }).finally(() => {
                popup.hide()
            })
        },

        generateToken(secret) {
            this.confirmDialog = {
                show: true,
                title: this.$t('apiUserGenerateToken'),
                message: `${this.$t('areYouSure')}?`,
                variant: 'success',
                actionLabel: this.$t('apiUserGenerateToken'),
                action: () => this.doGenerateToken(secret)
            }
        },

        doGenerateToken(secret) {
            this.confirmDialog.show = false
            const popup = this.$loadingPopup()
            this.$http.post("/v1/user/api-user/secret/" + secret.uuid + "/token", {
                user_uuid: this.$route.params.id
            }).then(async (resp) => {
                await navigator.clipboard.writeText(resp.data)
                this.$bvToast.toast(this.$t('savedToClipboard'), {
                    variant: 'success',
                    title: "Success"
                })
            }).catch(error => {
                if (error.response) {
                    if (error.response.status == 500) {
                        this.$serverErrorModal(error)
                        return
                    }
                    this.$mainDialog.msgBoxOk(error.response.data.message)
                }
            }).finally(() => {
                popup.hide()
            })
        },

        revoke(secret) {
            this.confirmDialog = {
                show: true,
                title: this.$t('revoke'),
                message: `${this.$t('areYouSure')}?`,
                variant: 'danger',
                actionLabel: this.$t('revoke'),
                action: () => this.doRevoke(secret)
            }
        },

        doRevoke(secret) {
            this.confirmDialog.show = false
            const popup = this.$loadingPopup()
            this.$http.delete("/v1/user/api-user/secret/" + secret.uuid).then(async () => {
                this.model.secrets = this.model.secrets.filter(s => s.uuid != secret.uuid)
                const { data } = await this.$http.get("/v1/user/api-user/" + this.$route.params.id)
                this.model.secrets = data.secrets
            }).catch(error => {
                if (error.response) {
                    if (error.response.status == 500) {
                        this.$serverErrorModal(error)
                        return
                    }
                    this.$mainDialog.msgBoxOk(error.response.data.message)
                }
            }).finally(() => {
                popup.hide()
            })
        },

        remove(e) {
            e.preventDefault()
            this.confirmDialog = {
                show: true,
                title: this.$t('delete'),
                message: `${this.$t('areYouSure')}?`,
                variant: 'danger',
                actionLabel: this.$t('delete'),
                action: () => this.doDelete()
            }
        },

        doDelete() {
            this.confirmDialog.show = false
            this.$http.delete("/v1/user/api-user/" + this.$route.params.id).then((resp) => {
                if (resp.status == 200) {
                    this.$router.push({ path: "/admin/iam" })
                }
            }).catch(error => {
                if (error.response) {
                    if (error.response.status == 500) {
                        this.$serverErrorModal(error)
                        return
                    }
                    this.$mainDialog.msgBoxOk(error.response.data.message)
                }
            })
        },

        fieldError(error) {
            let found = this.errors.find((i) => i.field == error.field)
            if (found === undefined) {
                this.errors.push(error)
            } else {
                this.errors.map(e => {
                    if (found.field == e.field)
                        e.error = error.error
                })
            }
        },

        checkForErrors() {
            return this.errors.find((i) => i.error)
        }
    },

    mounted() {
        this.$store.dispatch("fetchRoles").catch(error => {
            if (error.response && error.response.status == 500) {
                this.$serverErrorModal(error)
                return
            }
            if (error.response) {
                this.$mainDialog.msgBoxOk(error.response.data.message)
            }
        })

        this.$http.get("/v1/user/api-user/" + this.$route.params.id).then((resp) => {
            this.model = resp.data
            this.model.roles = this.model.roles.map(r => r.role)
            this.ready = true
        }).catch(error => {
            if (error.response && error.response.status == 500) {
                this.$serverErrorModal(error)
                return
            }
            if (error.response) {
                this.$mainDialog.msgBoxOk(error.response.data.message)
            }
        })
    }
}
</script>
